import classNames from "classnames";
import { useRef } from "react";
import useListFilters from "../../hooks/useListFilters";
import { useAdminUserMembers } from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { getRemoteDataStatus } from "../../utils";
import PermissionTable from "../PermissionTable";
import ExpandableRow from "../ui/ExpandableRow";
import Table from "../ui/Table";
import styles from "./AdminUserAssignments.module.css";

interface Props {
  userId: string;
}

const AdminUserAssignments = ({ userId }: Props) => {
  const { i18n } = useTranslations();
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { data, isValidating, error } = useAdminUserMembers(
    userId,
    filters.state,
    aborter.current.signal,
  );
  const members = data?.items ?? [];
  const hasNextPage = data?.next_page !== undefined;
  const status = getRemoteDataStatus({ isValidating, error });

  return (
    <Table
      remoteDataStatus={status}
      filters={filters}
      hasNextPage={hasNextPage}
      head={
        <ExpandableRow
          CellType="th"
          cells={[
            <th className={styles.alignLeft}>
              {i18n.admin.assignments.columnHeaders.member}
            </th>,
            <th className={styles.alignLeft}>
              {i18n.admin.assignments.columnHeaders.companies}
            </th>,
          ]}
        />
      }
      body={members.map((m) => {
        const assignedCompanies =
          m.companies?.filter(
            (c) => c.permissions && c.permissions.length > 0,
          ) ?? [];
        return (
          <ExpandableRow
            key={m.id}
            CellType="td"
            cells={[
              <td>{m.name}</td>,
              <td>
                {m.permissions
                  ? i18n.allCompanies
                  : i18n.companies(assignedCompanies.length)}
              </td>,
            ]}
            expandedContent={
              <Table
                isSelectable={false}
                total={(m.permissions ? 1 : 0) + assignedCompanies.length}
                head={
                  <tr>
                    <th
                      className={classNames(styles.company, styles.alignLeft)}
                    >
                      {i18n.admin.assignments.columnHeaders.company}
                    </th>
                    <th className={styles.alignLeft}>
                      {i18n.admin.assignments.columnHeaders.access}
                    </th>
                  </tr>
                }
                body={[
                  ...(m.permissions
                    ? [
                        <tr key="all-companies">
                          <td>{i18n.allCompanies}</td>
                          <td>
                            <PermissionTable permissions={m.permissions} />
                          </td>
                        </tr>,
                      ]
                    : []),
                  ...assignedCompanies.map((c) => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>
                        {c.permissions && (
                          <PermissionTable permissions={c.permissions} />
                        )}
                      </td>
                    </tr>
                  )),
                ]}
              />
            }
          />
        );
      })}
    />
  );
};

export default AdminUserAssignments;
